/**
 * Shared video embed URL utilities.
 * Converts various video platform URLs into embeddable iframe URLs.
 */

/**
 * Extract a YouTube video ID from any common YouTube URL format.
 * Returns null if the URL is not a recognized YouTube URL.
 */
export function extractYouTubeVideoId(url: string): string | null {
  if (!url) return null;

  // Already an embed URL — extract ID from path
  const embedMatch = url.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (embedMatch) return embedMatch[1];

  const nocookieMatch = url.match(/youtube-nocookie\.com\/embed\/([a-zA-Z0-9_-]+)/);
  if (nocookieMatch) return nocookieMatch[1];

  // youtu.be/VIDEO_ID
  if (url.includes('youtu.be/')) {
    const id = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
    if (id) return id;
  }

  // youtube.com/watch?v=VIDEO_ID
  if (url.includes('youtube.com/watch')) {
    try {
      const urlObj = new URL(url);
      const id = urlObj.searchParams.get('v');
      if (id) return id;
    } catch { /* fall through */ }
  }

  // youtube.com/shorts/VIDEO_ID
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return shortsMatch[1];

  // youtube.com/live/VIDEO_ID
  const liveMatch = url.match(/youtube\.com\/live\/([a-zA-Z0-9_-]+)/);
  if (liveMatch) return liveMatch[1];

  return null;
}

/**
 * Convert any YouTube URL to an embed URL.
 * Returns null if the URL is not a recognized YouTube URL.
 * If the URL is already an embed URL, returns it as-is.
 */
export function getYouTubeEmbedUrl(url: string): string | null {
  // Already an embed URL — return as-is
  if (url.includes('youtube.com/embed/') || url.includes('youtube-nocookie.com/embed/')) {
    return url;
  }
  const videoId = extractYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

/**
 * Check if a URL is a YouTube URL of any kind.
 */
export function isYouTubeUrl(url: string): boolean {
  if (!url) return false;
  return /(?:youtube\.com|youtu\.be|youtube-nocookie\.com)/.test(url);
}

/**
 * Convert a video URL (YouTube, Vimeo, or direct) to an embeddable URL.
 * Returns the embed URL, or null if the URL is not a recognized video platform.
 * Direct video URLs (.mp4, .webm, .ogg) are returned as-is (handled by <video> tag).
 */
export function getVideoEmbedUrl(url: string): string | null {
  if (!url) return null;

  // YouTube
  const ytEmbed = getYouTubeEmbedUrl(url);
  if (ytEmbed) return ytEmbed;

  // Vimeo player embed — pass through
  if (url.includes('player.vimeo.com')) return url;

  // Vimeo regular URL → embed
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;

  // Mux — player.mux.com (iframe embed), stream.mux.com (HLS / playback URL)
  const muxMatch = url.match(/(?:player|stream)\.mux\.com\/([a-zA-Z0-9]+)/);
  if (muxMatch) return `https://player.mux.com/${muxMatch[1]}`;

  // Not a recognized embed — return null (caller decides: <video> tag or skip)
  return null;
}
